import React, { useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Moon, BookOpen, LogOut, Sparkles, Sun, Library as LibraryIcon, Crown, Tag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getTodayDraw } from "@/lib/daily";
import { MoonPhase } from "@/components/MoonPhase";
import { UpgradeModal } from "@/components/UpgradeModal";
import { api } from "@/lib/api";
import { useHideOnScroll } from "@/hooks/useHideOnScroll";

export const Navbar = () => {
  const { user, login, logout, entitlements, entitlementsStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const headerRef = useRef(null);
  const hidden = useHideOnScroll({ headerRef, keepVisible: focusWithin || upgradeOpen, resetKey: location.pathname });

  // Pin the header only for keyboard focus inside it, and stop Tab from scroll-jumping to the hidden header.
  React.useEffect(() => {
    const header = headerRef.current;
    let savedY = null;
    const sync = () => {
      const a = document.activeElement;
      setFocusWithin(Boolean(a && header.contains(a) && a.matches(":focus-visible")));
    };
    const onKeyDown = (e) => { if (e.key === "Tab") savedY = window.scrollY; };
    const onFocusIn = () => {
      sync();
      if (savedY !== null && header.contains(document.activeElement) && window.scrollY !== savedY) window.scrollTo(0, savedY);
      savedY = null;
    };
    const onFocusOut = () => setTimeout(sync, 0);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
  // Re-check on every route change so the dot clears after drawing.
  const dailyAvailable = React.useMemo(() => !getTodayDraw(), [location.pathname]);

  // We only know a user's plan once entitlements have loaded. Until then the
  // state is unknown — never fall back to the free/Upgrade view prematurely.
  const entitlementsKnown = entitlements !== null;
  const isPremium = entitlements?.is_premium;
  const remaining = isPremium
    ? entitlements?.premium_remaining
    : entitlements?.complimentary_remaining;

  const manageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = res.data.portal_url;
    } catch (e) {
      toast.error("Could not open the billing portal.");
      setPortalLoading(false);
    }
  };

  return (
    <header
      ref={headerRef}
      className={`sticky top-0 z-40 border-b border-[#D9A441]/20 bg-[#0B0C15]/70 backdrop-blur-xl transition-transform duration-300 ease-out motion-reduce:transition-none ${hidden ? "-translate-y-full" : "translate-y-0"}`}
      data-testid="navbar"
      data-hidden={hidden ? "true" : "false"}
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-2 gap-y-2 px-3 py-3 sm:px-8 lg:flex-nowrap lg:py-4">
        <button
          onClick={() => navigate("/")}
          className="group flex items-center gap-2.5 lg:mr-auto"
          data-testid="nav-home-logo"
        >
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full border border-[#D9A441]/50 bg-[#131422]">
            <Moon className="h-4 w-4 text-[#D9A441]" />
            <Sparkles className="absolute -right-1 -top-1 h-3 w-3 text-[#E5C365]" />
          </span>
          <span className="font-display text-xl font-semibold tracking-tight text-[#F8F3E6] whitespace-nowrap sm:text-2xl">
            The <span className="text-[#D9A441]">Cardlight</span>
          </span>
        </button>

        <nav aria-label="Main" data-testid="nav-links" className="order-3 flex w-full items-center justify-around border-t border-[#D9A441]/10 pt-2 sm:justify-center sm:gap-3 lg:order-none lg:w-auto lg:justify-end lg:border-0 lg:pt-0">
          <MoonPhase />
          <button
            onClick={() => navigate("/daily")}
            data-testid="nav-daily-btn"
            aria-label="Daily Ritual"
            className={`relative flex items-center gap-2 rounded-full px-2.5 py-2 text-sm font-medium transition-colors sm:px-4 ${
              location.pathname === "/daily"
                ? "bg-[#D9A441]/15 text-[#E5C365]"
                : "text-[#A3A5B8] hover:text-[#F8F3E6]"
            }`}
          >
            <Sun className="h-4 w-4" />
            <span className="hidden sm:inline">Daily</span>
            {dailyAvailable && (
              <span
                className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#D9A441] sm:right-2.5"
                style={{ boxShadow: "0 0 8px rgba(217,164,65,0.8)" }}
                data-testid="daily-available-dot"
              />
            )}
          </button>
          <button
            onClick={() => navigate("/library")}
            data-testid="nav-library-btn"
            aria-label="Card library"
            className={`flex items-center gap-2 rounded-full px-2.5 py-2 text-sm font-medium transition-colors sm:px-4 ${
              location.pathname === "/library"
                ? "bg-[#D9A441]/15 text-[#E5C365]"
                : "text-[#A3A5B8] hover:text-[#F8F3E6]"
            }`}
          >
            <LibraryIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Library</span>
          </button>
          <button
            onClick={() => navigate("/journal")}
            data-testid="nav-journal-btn"
            aria-label="Journal"
            className={`flex items-center gap-2 rounded-full px-2.5 py-2 text-sm font-medium transition-colors sm:px-4 ${
              location.pathname === "/journal"
                ? "bg-[#D9A441]/15 text-[#E5C365]"
                : "text-[#A3A5B8] hover:text-[#F8F3E6]"
            }`}
          >
            <BookOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Journal</span>
          </button>
          <button
            onClick={() => navigate("/pricing")}
            data-testid="nav-pricing-btn"
            aria-label="Pricing"
            className={`flex items-center gap-2 rounded-full px-2.5 py-2 text-sm font-medium transition-colors sm:px-4 ${
              location.pathname === "/pricing"
                ? "bg-[#D9A441]/15 text-[#E5C365]"
                : "text-[#A3A5B8] hover:text-[#F8F3E6]"
            }`}
          >
            <Tag className="h-4 w-4" />
            <span className="hidden sm:inline">Pricing</span>
          </button>

        </nav>

        <div className="order-2 flex items-center lg:order-none lg:ml-3" data-testid="nav-account">
          {user ? (
            <div className="flex items-center gap-2.5 sm:gap-3">
              {!entitlementsKnown ? (
                <span
                  data-testid="entitlement-loading"
                  aria-label="Loading your plan"
                  className="flex items-center gap-1.5 rounded-full border border-[#D9A441]/25 bg-[#131422]/70 px-3 py-1.5 text-xs font-medium text-[#A3A5B8]"
                >
                  <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                  <span className="hidden sm:inline">Loading…</span>
                </span>
              ) : isPremium ? (
                <button
                  onClick={manageSubscription}
                  disabled={portalLoading}
                  data-testid="nav-premium-badge"
                  title="Manage subscription"
                  className="flex items-center gap-1.5 rounded-full border border-[#D9A441]/50 bg-[#D9A441]/10 px-3 py-1.5 text-xs font-medium text-[#E5C365] transition-all hover:bg-[#D9A441]/20 disabled:opacity-60"
                >
                  <Crown className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Premium</span>
                  {typeof remaining === "number" && (
                    <span className="rounded-full bg-[#D9A441]/20 px-1.5 py-0.5 text-[10px] text-[#E5C365]" data-testid="usage-chip">
                      {remaining}/{entitlements?.premium_allowance}
                    </span>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => setUpgradeOpen(true)}
                  data-testid="nav-upgrade-btn"
                  className="flex items-center gap-1.5 rounded-full border border-[#D9A441]/50 bg-gradient-to-r from-[#D9A441]/15 to-[#1a1426] px-3 py-1.5 text-xs font-medium text-[#E5C365] transition-all hover:shadow-[0_0_16px_rgba(217,164,65,0.25)]"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Upgrade</span>
                  {typeof remaining === "number" && (
                    <span className="rounded-full bg-[#D9A441]/15 px-1.5 py-0.5 text-[10px] text-[#A3A5B8]" data-testid="usage-chip">
                      {remaining} left
                    </span>
                  )}
                </button>
              )}
              <div className="hidden items-center gap-2.5 min-[360px]:flex" data-testid="nav-user">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="h-8 w-8 rounded-full border border-[#D9A441]/40 object-cover"
                  />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D9A441]/40 bg-[#131422] text-sm text-[#D9A441]">
                    {user.name?.[0]?.toUpperCase() || "?"}
                  </span>
                )}
                <span className="hidden max-w-[10rem] truncate whitespace-nowrap text-sm text-[#F8F3E6] md:inline lg:hidden xl:inline" data-testid="nav-user-name">{user.name}</span>
              </div>
              <button
                onClick={logout}
                data-testid="nav-logout-btn"
                className="flex h-9 w-9 items-center justify-center rounded-full text-[#A3A5B8] transition-colors hover:bg-[#131422] hover:text-[#F8F3E6]"
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={login}
              data-testid="nav-login-btn"
              className="rounded-full border border-[#D9A441]/50 bg-[#D9A441]/10 whitespace-nowrap px-3 py-2 text-sm font-medium text-[#E5C365] transition-all hover:bg-[#D9A441]/20 hover:shadow-[0_0_18px_rgba(217,164,65,0.25)] sm:px-4"
            >
              Sign in
            </button>
          )}
        </div>
      </div>
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </header>
  );
};
